import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  SalesOrderBulkPrintResult,
  SalesOrderBulkResult,
  SalesOrderFormOptions,
  SalesOrderListItem,
  SalesOrderListResult,
} from "@cx/contracts";
import { MAX_BULK_ORDERS, formatBRL, formatDateBR } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { bulkSummary } from "./labels";

/** Pedidos de venda (RF-VEN-02): filtros, seleção e ações em lote (mudar situação, imprimir). */
export function SalesOrdersListPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState("");
  const [statusId, setStatusId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState<Map<string, SalesOrderListItem>>(new Map());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [failures, setFailures] = useState<{ number: number; message: string }[]>([]);
  const term = q.trim();
  const canCreate = can(perms, "sales_orders", "create");
  const canUpdate = can(perms, "sales_orders", "update");
  // Situações vêm das opções do formulário; quem só lê fica sem o filtro de situação.
  const options = useQuery({
    queryKey: ["sales-orders", companyId, "form-options"],
    enabled: canCreate || canUpdate,
    queryFn: () => api<SalesOrderFormOptions>("/sales-orders/form-options"),
  });
  const list = useInfiniteQuery({
    queryKey: ["sales-orders", companyId, "list", term, statusId, from, to],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<SalesOrderListResult>(
        `/sales-orders?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(statusId ? { statusId } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const summary = list.data?.pages[0]?.summary;
  const create = () => navigate({ to: "/vendas/pedidos/novo" });
  const toggle = (r: SalesOrderListItem) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(r.id)) next.delete(r.id);
      else next.set(r.id, r);
      return next;
    });
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allChecked) for (const r of rows) next.delete(r.id);
      else for (const r of rows) next.set(r.id, r);
      return next;
    });
  const picked = [...selected.values()];
  const tooMany = picked.length > MAX_BULK_ORDERS;

  const bulkStatus = useMutation({
    mutationFn: () =>
      api<SalesOrderBulkResult>("/sales-orders/bulk/change-status", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({
          toStatusId: target,
          items: picked.map((r) => ({ id: r.id, expectedFromStatusId: r.status.id })),
        }),
      }),
    onSuccess: (r) => {
      const byId = new Map(picked.map((p) => [p.id, p.number]));
      const failed = r.results.filter((x) => !x.ok);
      setFailures(failed.map((x) => ({ number: byId.get(x.id) ?? 0, message: x.message ?? "Erro" })));
      toast(r.failCount ? "warning" : "success", bulkSummary(r));
      setSelected(new Map(picked.filter((p) => failed.some((f) => f.id === p.id)).map((p) => [p.id, p])));
      setBulkOpen(false);
      qc.invalidateQueries({ queryKey: ["sales-orders", companyId] });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const bulkPrint = useMutation({
    mutationFn: () =>
      api<SalesOrderBulkPrintResult>("/sales-orders/bulk/print", {
        method: "POST",
        body: JSON.stringify({ ids: picked.map((r) => r.id) }),
      }),
    onSuccess: (r) => {
      if (r.missing.length)
        toast("warning", `${r.missing.length} pedido(s) não encontrado(s) ficaram de fora.`);
      window.location.assign(r.url);
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });

  return (
    <>
      <ContentHeader
        title="Pedidos de venda"
        breadcrumb={["Vendas", "Pedidos de venda"]}
        actions={
          canCreate && (
            <button type="button" className="btn btn-primary" onClick={create}>
              Incluir pedido
            </button>
          )
        }
      />
      <div className="box card">
        <div className="row g-2 p-3 pb-0">
          <div className="col-md-4">
            <label className="visually-hidden" htmlFor="orders-q">
              Pesquisar pedido
            </label>
            <input
              id="orders-q"
              type="search"
              className="form-control"
              placeholder="Pesquisar por número ou cliente"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {options.data && (
            <div className="col-md-3">
              <label className="visually-hidden" htmlFor="orders-status">
                Situação
              </label>
              <select
                id="orders-status"
                className="form-select"
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <option value="">Todas as situações</option>
                {options.data.statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="orders-from">
              Data inicial
            </label>
            <input
              id="orders-from"
              type="date"
              className="form-control"
              title="Data inicial"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="visually-hidden" htmlFor="orders-to">
              Data final
            </label>
            <input
              id="orders-to"
              type="date"
              className="form-control"
              title="Data final"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
        {picked.length > 0 && (
          <div
            className="d-flex flex-wrap align-items-center gap-2 px-3 pt-3"
            role="toolbar"
            aria-label="Ações em lote"
          >
            <span className="small" data-testid="orders-selected">
              {picked.length} {picked.length === 1 ? "selecionado" : "selecionados"}
            </span>
            {canUpdate && options.data && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                disabled={tooMany || bulkStatus.isPending}
                onClick={() => {
                  setTarget("");
                  setBulkOpen(true);
                }}
              >
                Alterar situação
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={tooMany || bulkPrint.isPending}
              onClick={() => bulkPrint.mutate()}
            >
              {bulkPrint.isPending ? "Gerando PDF…" : "Imprimir"}
            </button>
            <button type="button" className="btn btn-sm btn-link" onClick={() => setSelected(new Map())}>
              Limpar seleção
            </button>
            {tooMany && (
              <span className="text-danger small">No máximo {MAX_BULK_ORDERS} pedidos por vez.</span>
            )}
          </div>
        )}
        {failures.length > 0 && (
          <div className="alert alert-warning m-3 mb-0" role="alert" data-testid="bulk-failures">
            <strong>Pedidos não alterados:</strong>
            <ul className="mb-0">
              {failures.map((f) => (
                <li key={f.number}>
                  Nº {f.number}: {f.message}
                </li>
              ))}
            </ul>
          </div>
        )}
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhum pedido"
            description="Registre pedidos com os preços da lista do cliente, reserve e lance o estoque e gere as contas a receber."
            actions={canCreate ? [{ label: "Incluir pedido", onClick: create }] : []}
          />
        )}
        {rows.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Pedidos de venda">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        aria-label="Selecionar todos"
                        checked={allChecked}
                        onChange={toggleAll}
                      />
                    </th>
                    <th scope="col">Número</th>
                    <th scope="col">Data</th>
                    <th scope="col">Cliente</th>
                    <th scope="col">Vendedor</th>
                    <th scope="col" className="text-end">
                      Total
                    </th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          aria-label={`Selecionar pedido ${r.number}`}
                          checked={selected.has(r.id)}
                          onChange={() => toggle(r)}
                        />
                      </td>
                      <td>
                        <Link to="/vendas/pedidos/$id" params={{ id: r.id }}>
                          {r.number}
                        </Link>
                      </td>
                      <td>{formatDateBR(r.date)}</td>
                      <td>{r.contactName}</td>
                      <td>{r.sellerName ?? "—"}</td>
                      <td className="text-end">{formatBRL(r.totalCents)}</td>
                      <td>
                        <StatusBadge tone={r.status.color}>{r.status.label}</StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 p-3">
              {summary && (
                <span className="text-muted small" data-testid="orders-summary">
                  {summary.count} {summary.count === 1 ? "pedido" : "pedidos"}
                  {summary.totalCents !== null && ` · total ${formatBRL(summary.totalCents)}`}
                </span>
              )}
              {list.hasNextPage && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={list.isFetchingNextPage}
                  onClick={() => list.fetchNextPage()}
                >
                  Carregar mais
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {bulkOpen && options.data && (
        <>
          <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="bulk-status-title">
            <div className="modal-dialog">
              <form
                className="modal-content"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (target) bulkStatus.mutate();
                }}
              >
                <div className="modal-header">
                  <h2 className="modal-title h5" id="bulk-status-title">
                    Alterar situação de {picked.length} {picked.length === 1 ? "pedido" : "pedidos"}
                  </h2>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Fechar"
                    onClick={() => setBulkOpen(false)}
                  />
                </div>
                <div className="modal-body">
                  <label className="form-label" htmlFor="bulk-status-target">
                    Nova situação
                  </label>
                  <select
                    id="bulk-status-target"
                    className="form-select"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    required
                  >
                    <option value="">Selecione</option>
                    {options.data.statuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                  <p className="form-text mb-0">
                    Cada pedido muda em separado: os que não permitirem a mudança ficam como estão e aparecem
                    na lista de erros.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setBulkOpen(false)}
                  >
                    Voltar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!target || bulkStatus.isPending}
                  >
                    {bulkStatus.isPending ? "Alterando…" : "Alterar"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal-backdrop show" />
        </>
      )}
    </>
  );
}
